// Pure helpers for the in-call on-demand Record / Stop control.
//
// Kept dependency-free so the record-button behaviour can be unit-tested next
// to the other lib helpers (lib/*.test.js). The server is the source of truth
// for the actual recording bool and enforces mode + entitlement + ownership;
// these helpers only derive what the button should LOOK like and whether the
// user may click it.
//
// Key compliance invariant honoured here: the red dot (●) is shown ONLY when a
// recording is actually active (isRecording === true) and there is no error.
// When a start fails (e.g. HTTP 502 announce_failed) the backend returns
// recording:false, so isRecording stays false and the control must render as
// "not recording" — never a red dot.

export function formatRecTime(seconds) {
  const s = Math.max(0, Math.floor((seconds || 0)));
  const m = Math.floor(s / 60);
  const sec = s - m * 60;
  return m + ':' + String(sec).padStart(2, '0');
}

/**
 * resolveRecordButton — compute the full record-button presentation + state.
 *
 * @param {Object} p
 * @param {boolean} p.supported  line mode & entitlement allow recording (server-gated; false after 403/409)
 * @param {boolean} p.connected  the call leg is actually connected
 * @param {boolean} p.translating live translation owns the conference
 * @param {boolean} p.busy       a start/stop request is in flight
 * @param {boolean} p.isRecording currently recording (server truth)
 * @param {number}  p.seconds    elapsed recording seconds (drives the ● timer)
 * @param {string|null} p.error  human-readable failure (e.g. 'announce_failed'), null when healthy
 *
 * @returns {{visible:boolean, disabled:boolean, locked:boolean, busy:boolean,
 *            title:string, ariaLabel:string, label:string, redDot:boolean}}
 */
export function resolveRecordButton({
  supported,
  connected,
  translating,
  busy,
  isRecording,
  seconds = 0,
  error = null,
}) {
  // Hidden entirely when the line mode is Off / not permitted (server 403/409
  // turns supported off), or while there is no call at all.
  const visible = !!supported && !!connected;

  const locked = !connected || !!translating;
  const disabled = locked || !!busy;

  // The red dot (active recording) exists ONLY when recording is real and the
  // last attempt did not error — so a 502 announce_failed NEVER paints a dot.
  const redDot = !!isRecording && !error;

  let title;
  if (translating) {
    title = 'Not available during live translation - the translation engine owns the conference';
  } else if (busy) {
    title = isRecording ? 'Stopping recording…' : 'Starting recording…';
  } else if (redDot) {
    title = 'Stop recording';
  } else if (error) {
    title = 'Recording unavailable: ' + error;
  } else if (!connected) {
    title = 'Available once the call connects';
  } else {
    title = 'Record this call';
  }

  const ariaLabel = redDot ? 'Stop recording' : 'Record this call';
  const label = redDot ? '● ' + formatRecTime(seconds) : 'Record';

  return { visible, disabled, locked, busy, title, ariaLabel, label, redDot, error };
}